import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Auth from './auth/Auth'; // Component to verify existing PIN
import SecurityPin from '../doctor/dashboard/popups/SecurityPin'; // Component to set/update PIN
import { getDoctorPreferences } from '../../routes/doctor/patients';
import { setNotificationMessage } from '../../redux/features/general/notification';
import { setIsSecurityPinPopupOpened } from '../../redux/features/general/navigation';

const LockScreenContent: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const colors = useAppSelector(state => state.theme.colors);
    const location = useLocation();

    const isSecurityPinPopupOpened = useAppSelector(state => state.navigation.isSecurityPinPopupOpened);
    const [hasPin, setHasPin] = useState<boolean | undefined>(undefined); // undefined: loading, true: has PIN, false: no PIN
    const [isLoadingPinStatus, setIsLoadingPinStatus] = useState(true);

    useEffect(() => {
        const checkPinStatus = async () => {
            setIsLoadingPinStatus(true);
            try {
                const res = await getDoctorPreferences();
                if (res?.data?.status === 200) {
                    if (!res.data.doctorPreferences.securityPin) {
                        setHasPin(false);
                        dispatch(setIsSecurityPinPopupOpened(true)); // Open popup to set PIN
                    } else {
                        setHasPin(true);
                        dispatch(setIsSecurityPinPopupOpened(false)); // Ensure it's closed if a PIN exists
                    }
                } else {
                    dispatch(setNotificationMessage(t('errorFetchingPreferences')));
                    navigate('/login');
                }
            } catch (error) {
                console.error("Error checking PIN status:", error);
                dispatch(setNotificationMessage(t('errorFetchingPreferences')));
                navigate('/login');
            } finally {
                setIsLoadingPinStatus(false);
            }
        };

        checkPinStatus();
    }, [dispatch, navigate, t]);

    // Function to handle closing SecurityPin popup from LockScreen context
    const handleSecurityPinPopupClose = (pinSetSuccessfully: boolean) => {
        dispatch(setIsSecurityPinPopupOpened(false));
        // Regardless of whether PIN was set or not, unlock the screen and navigate back
        localStorage.setItem('isScreenLocked', 'off');
        const previousLocation = localStorage.getItem('lockScreenRedirect');
        navigate(previousLocation || '/dashboard');
        localStorage.removeItem('lockScreenRedirect');
    };

    if (isLoadingPinStatus) {
        // Show a loading state while checking for PIN
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                width: '100vw',
                backgroundColor: colors?.interface,
                color: colors?.text
            }}>
                <CircularProgress sx={{ color: colors?.main }} />
                <Box sx={{ marginTop: 2 }}>{t('loading')}</Box>
            </Box>
        );
    }

    return (
        <Box
            component="main"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                width: '100vw',
                backgroundColor: colors?.interface,
            }}
        >
            {hasPin && !isSecurityPinPopupOpened && <Auth />} {/* Only render Auth if a PIN exists AND the SecurityPin popup is not open */}

            {isSecurityPinPopupOpened && (
                <SecurityPin 
                    isLockScreenContext={true} // Always true when opened from LockScreenContent
                    onClose={handleSecurityPinPopupClose} // Pass custom close handler
                />
            )}
        </Box>
    );
};

export default LockScreenContent;